import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../../api.js';
import { Wrench, X, FileText, Plus, Car, MapPin } from 'lucide-react';
import { useOrganizations } from '../../../index.js';
import ToolDocumentForm from './ToolDocumentForm.jsx';

function ToolForm({ isOpen, onClose, onSuccess, onMessage, editingTool }) {
  const { t } = useTranslation();
  const { data: organizations = [] } = useOrganizations();

  // Fetch vehicles and locations for assignment
  const { data: vehicles = [] } = useQuery({
    queryKey: ['vehicles'],
    queryFn: async () => {
      const response = await api.vehicle.getAll();
      return response.data || [];
    },
  });

  const { data: workingSites = [] } = useQuery({
    queryKey: ['workingSites'],
    queryFn: async () => {
      const response = await api.workingSite.getAll();
      return response.data || [];
    },
  });

  // Basic Information
  const [orgId, setOrgId] = useState('');
  const [toolCode, setToolCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('other');
  const [toolType, setToolType] = useState('');

  // Manufacturer/Model Details
  const [manufacturer, setManufacturer] = useState('');
  const [model, setModel] = useState('');
  const [version, setVersion] = useState('');
  const [serialNumber, setSerialNumber] = useState('');

  // Features
  const [isCordless, setIsCordless] = useState(false);
  const [hasTracker, setHasTracker] = useState(false);
  const [trackerId, setTrackerId] = useState('');

  // Purchase Information
  const [purchaseDate, setPurchaseDate] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [warrantyExpirationDate, setWarrantyExpirationDate] = useState('');

  // Status & Condition
  const [condition, setCondition] = useState('good');
  const [status, setStatus] = useState('active');
  const [photoUrl, setPhotoUrl] = useState('');

  // Maintenance
  const [lastMaintenanceDate, setLastMaintenanceDate] = useState('');
  const [nextMaintenanceDate, setNextMaintenanceDate] = useState('');
  const [notes, setNotes] = useState('');

  // Document modal
  const [showDocumentForm, setShowDocumentForm] = useState(false);
  const [pendingDocument, setPendingDocument] = useState(null);

  // Assignment
  const [assignedToType, setAssignedToType] = useState('unassigned');
  const [assignedToId, setAssignedToId] = useState('');

  useEffect(() => {
    if (editingTool) {
      setOrgId(editingTool.org_id || '');
      setToolCode(editingTool.tool_code || '');
      setName(editingTool.name || '');
      setDescription(editingTool.description || '');
      setCategory(editingTool.category || 'other');
      setToolType(editingTool.tool_type || '');
      setManufacturer(editingTool.manufacturer || '');
      setModel(editingTool.model || '');
      setVersion(editingTool.version || '');
      setSerialNumber(editingTool.serial_number || '');
      setIsCordless(editingTool.is_cordless || false);
      setHasTracker(editingTool.has_tracker || false);
      setTrackerId(editingTool.tracker_id || '');
      setPurchaseDate(editingTool.purchase_date || '');
      setPurchasePrice(editingTool.purchase_price || '');
      setWarrantyExpirationDate(editingTool.warranty_expiration_date || '');
      setCondition(editingTool.condition || 'good');
      setStatus(editingTool.status || 'active');
      setPhotoUrl(editingTool.photo_url || '');
      setLastMaintenanceDate(editingTool.last_maintenance_date || '');
      setNextMaintenanceDate(editingTool.next_maintenance_date || '');
      setNotes(editingTool.notes || '');
      setAssignedToType(editingTool.assigned_to_type || 'unassigned');
      setAssignedToId(editingTool.assigned_to_id || '');
    } else {
      resetForm();
    }
  }, [editingTool, isOpen]);

  const resetForm = () => {
    setOrgId(organizations.length > 0 ? organizations[0].code : '');
    setToolCode('');
    setName('');
    setDescription('');
    setCategory('other');
    setToolType('');
    setManufacturer('');
    setModel('');
    setVersion('');
    setSerialNumber('');
    setIsCordless(false);
    setHasTracker(false);
    setTrackerId('');
    setPurchaseDate('');
    setPurchasePrice('');
    setWarrantyExpirationDate('');
    setCondition('good');
    setStatus('active');
    setPhotoUrl('');
    setLastMaintenanceDate('');
    setNextMaintenanceDate('');
    setNotes('');
    setAssignedToType('unassigned');
    setAssignedToId('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!orgId || !toolCode || !name) {
      onMessage && onMessage({
        title: t('tools.toast.error'),
        message: t('tools.form.errors.requiredFields'),
        type: 'error'
      });
      return;
    }

    try {
      const toolData = {
        org_id: orgId,
        tool_code: toolCode,
        name: name,
        description: description || null,
        category: category,
        tool_type: toolType || null,
        manufacturer: manufacturer || null,
        model: model || null,
        version: version || null,
        serial_number: serialNumber || null,
        is_cordless: isCordless,
        has_tracker: hasTracker,
        tracker_id: trackerId || null,
        purchase_date: purchaseDate || null,
        purchase_price: purchasePrice ? parseFloat(purchasePrice) : null,
        warranty_expiration_date: warrantyExpirationDate || null,
        condition: condition,
        status: status,
        photo_url: photoUrl || null,
        assigned_to_type: assignedToType,
        assigned_vehicle_id: assignedToType === 'vehicle' && assignedToId ? parseInt(assignedToId) : null,
        assigned_location_id: assignedToType === 'location' && assignedToId ? parseInt(assignedToId) : null,
        last_maintenance_date: lastMaintenanceDate || null,
        next_maintenance_date: nextMaintenanceDate || null,
        notes: notes || null
      };

      if (editingTool) {
        // Update existing tool
        await api.tool.update(editingTool.id, toolData);
        onMessage && onMessage({
          title: t('tools.toast.success'),
          message: t('tools.messages.toolUpdated'),
          type: 'success'
        });
      } else {
        // Create new tool
        const response = await api.tool.create(toolData);
        const newToolId = response.data?.id;

        // If there's a pending document, create it
        if (pendingDocument && newToolId) {
          try {
            await api.toolDocument.create({
              ...pendingDocument,
              tool_id: newToolId
            });
            onMessage && onMessage({
              title: t('tools.toast.success'),
              message: t('tools.messages.toolAndDocumentAdded'),
              type: 'success'
            });
          } catch (docError) {
            console.error('Error creating document:', docError);
            onMessage && onMessage({
              title: t('tools.toast.warning'),
              message: t('tools.messages.toolCreatedDocumentFailed'),
              type: 'warning'
            });
          }
        } else {
          onMessage && onMessage({
            title: t('tools.toast.success'),
            message: t('tools.messages.toolAdded'),
            type: 'success'
          });
        }
      }

      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving tool:', error);
      onMessage && onMessage({
        title: t('tools.toast.error'),
        message: t('tools.messages.saveError', { error: error.message }),
        type: 'error'
      });
    }
  };

  const handleDocumentSubmit = (documentData) => {
    // Store the document data without tool_id (will be added after tool creation)
    setPendingDocument(documentData);
    setShowDocumentForm(false);
    onMessage && onMessage({
      title: t('tools.messages.documentReadyTitle'),
      message: t('tools.messages.documentReadyBody'),
      type: 'info'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-amber-500 text-white px-6 py-4 flex justify-between items-center sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <Wrench className="w-6 h-6" />
            <h2 className="text-xl font-bold">
              {editingTool ? t('tools.form.editTitle') : t('tools.form.createTitle')}
            </h2>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b pb-2">
              {t('tools.form.sections.basic')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.form.fields.organization')} *
                </label>
                <select
                  value={orgId}
                  onChange={(e) => setOrgId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                >
                  <option value="">{t('common.actions.select')}</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.code}>
                      {org.name} ({org.code})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.form.fields.toolCode')} *
                </label>
                <input
                  type="text"
                  value={toolCode}
                  onChange={(e) => setToolCode(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder={t('tools.form.placeholders.toolCode')}
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.form.fields.toolName')} *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder={t('tools.form.placeholders.toolName')}
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.form.fields.description')}
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows="2"
                  placeholder={t('tools.form.placeholders.description')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.form.fields.category')} *
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                >
                  <option value="power_tool">{t('tools.category.power_tool')}</option>
                  <option value="hand_tool">{t('tools.category.hand_tool')}</option>
                  <option value="measuring_equipment">{t('tools.category.measuring_equipment')}</option>
                  <option value="safety_equipment">{t('tools.category.safety_equipment')}</option>
                  <option value="diagnostic_equipment">{t('tools.category.diagnostic_equipment')}</option>
                  <option value="lifting_equipment">{t('tools.category.lifting_equipment')}</option>
                  <option value="cutting_tool">{t('tools.category.cutting_tool')}</option>
                  <option value="welding_equipment">{t('tools.category.welding_equipment')}</option>
                  <option value="other">{t('tools.category.other')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.form.fields.toolType')}
                </label>
                <input
                  type="text"
                  value={toolType}
                  onChange={(e) => setToolType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder={t('tools.form.placeholders.toolType')}
                />
              </div>
            </div>
          </div>

          {/* Manufacturer Details Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b pb-2">
              {t('tools.form.sections.manufacturer')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.form.fields.manufacturer')}
                </label>
                <input
                  type="text"
                  value={manufacturer}
                  onChange={(e) => setManufacturer(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder={t('tools.form.placeholders.manufacturer')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.form.fields.model')}
                </label>
                <input
                  type="text"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder={t('tools.form.placeholders.model')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.form.fields.version')}
                </label>
                <input
                  type="text"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder={t('tools.form.placeholders.version')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.form.fields.serialNumber')}
                </label>
                <input
                  type="text"
                  value={serialNumber}
                  onChange={(e) => setSerialNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder={t('tools.form.placeholders.serialNumber')}
                />
              </div>
            </div>
          </div>

          {/* Features Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b pb-2">
              {t('tools.form.sections.features')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isCordless"
                  checked={isCordless}
                  onChange={(e) => setIsCordless(e.target.checked)}
                  className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                />
                <label htmlFor="isCordless" className="text-sm font-medium text-gray-700">
                  {t('tools.form.fields.isCordless')}
                </label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="hasTracker"
                  checked={hasTracker}
                  onChange={(e) => setHasTracker(e.target.checked)}
                  className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                />
                <label htmlFor="hasTracker" className="text-sm font-medium text-gray-700">
                  {t('tools.form.fields.hasTracker')}
                </label>
              </div>

              {hasTracker && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('tools.form.fields.trackerId')}
                  </label>
                  <input
                    type="text"
                    value={trackerId}
                    onChange={(e) => setTrackerId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder={t('tools.form.placeholders.trackerId')}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Purchase Information Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b pb-2">
              {t('tools.form.sections.purchase')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.form.fields.purchaseDate')}
                </label>
                <input
                  type="date"
                  value={purchaseDate}
                  onChange={(e) => setPurchaseDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.form.fields.purchasePrice')}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={purchasePrice}
                  onChange={(e) => setPurchasePrice(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.form.fields.warrantyExpiration')}
                </label>
                <input
                  type="date"
                  value={warrantyExpirationDate}
                  onChange={(e) => setWarrantyExpirationDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            {/* Add Invoice/Document Button */}
            {!editingTool && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowDocumentForm(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  {pendingDocument ? (
                    <>
                      <FileText className="w-4 h-4" />
                      {t('tools.form.actions.documentAddedEdit')}
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      {t('tools.form.actions.addInvoiceDocument')}
                    </>
                  )}
                </button>
                {pendingDocument && (
                  <p className="text-sm text-green-600 mt-2">
                    {t('tools.form.documentReady', {
                      type: t(`tools.docType.${pendingDocument.doc_type}`, { defaultValue: pendingDocument.doc_type })
                    })}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Status & Condition Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b pb-2">
              {t('tools.form.sections.statusCondition')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.form.fields.condition')} *
                </label>
                <select
                  value={condition}
                  onChange={(e) => setCondition(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                >
                  <option value="new">{t('tools.condition.new')}</option>
                  <option value="excellent">{t('tools.condition.excellent')}</option>
                  <option value="good">{t('tools.condition.good')}</option>
                  <option value="fair">{t('tools.condition.fair')}</option>
                  <option value="poor">{t('tools.condition.poor')}</option>
                  <option value="broken">{t('tools.condition.broken')}</option>
                  <option value="lost">{t('tools.condition.lost')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.form.fields.status')} *
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                >
                  <option value="active">{t('tools.status.active')}</option>
                  <option value="in_maintenance">{t('tools.status.in_maintenance')}</option>
                  <option value="out_of_service">{t('tools.status.out_of_service')}</option>
                  <option value="retired">{t('tools.status.retired')}</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.form.fields.photoUrl')}
                </label>
                <input
                  type="url"
                  value={photoUrl}
                  onChange={(e) => setPhotoUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="https://example.com/tool-photo.jpg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('tools.form.hints.photoUrl')}
                </p>
              </div>
            </div>
          </div>

          {/* Assignment Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b pb-2">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                {t('tools.form.sections.assignment')}
              </div>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.form.fields.assignmentType')}
                </label>
                <select
                  value={assignedToType}
                  onChange={(e) => {
                    setAssignedToType(e.target.value);
                    setAssignedToId(''); // Reset assignedToId when type changes
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="unassigned">{t('tools.assignment.unassigned')}</option>
                  <option value="vehicle">{t('tools.assignment.vehicle')}</option>
                  <option value="location">{t('tools.assignment.site')}</option>
                </select>
              </div>

              {assignedToType === 'vehicle' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center gap-1">
                      <Car className="w-4 h-4" />
                      {t('tools.assignment.vehicle')}
                    </div>
                  </label>
                  <select
                    value={assignedToId}
                    onChange={(e) => setAssignedToId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">{t('tools.assignment.selectVehicle')}</option>
                    {vehicles.map((vehicle) => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.license_plate} - {vehicle.make} {vehicle.model}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {assignedToType === 'location' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {t('tools.assignment.site')}
                    </div>
                  </label>
                  <select
                    value={assignedToId}
                    onChange={(e) => setAssignedToId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">{t('tools.assignment.selectSite')}</option>
                    {workingSites.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.name} ({site.city || t('common.states.notAvailable')})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Maintenance Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b pb-2">
              {t('tools.form.sections.maintenance')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.form.fields.lastMaintenance')}
                </label>
                <input
                  type="date"
                  value={lastMaintenanceDate}
                  onChange={(e) => setLastMaintenanceDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.form.fields.nextMaintenance')}
                </label>
                <input
                  type="date"
                  value={nextMaintenanceDate}
                  onChange={(e) => setNextMaintenanceDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.form.fields.notes')}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows="3"
                  placeholder={t('tools.form.placeholders.notes')}
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
            >
              {editingTool ? t('tools.form.actions.update') : t('common.actions.add')}
            </button>
          </div>
        </form>
      </div>

      {/* Document Form Modal */}
      {showDocumentForm && (
        <ToolDocumentForm
          tool={{ id: null, org_id: orgId, name: name }}
          doc={pendingDocument}
          onClose={() => {
            setShowDocumentForm(false);
          }}
          onSubmit={handleDocumentSubmit}
          onMessage={onMessage}
        />
      )}
    </div>
  );
}

export default ToolForm;
